import type { AuditLogEntry, AuditLogResponse } from "@friends-church-dvr/contracts";

interface RenderOptions { app: HTMLDivElement; brand: string; menu: string }

const escapeHtml = (value: string): string => value.replace(/[&<>'"]/g, (character) => ({
  "&": "&amp;", "<": "&lt;", ">": "&gt;", "'": "&#39;", '"': "&quot;",
})[character] ?? character);

const formatTime = (value: string): string => new Intl.DateTimeFormat("en-US", {
  dateStyle: "medium", timeStyle: "short", hour12: true, timeZone: "America/New_York",
}).format(new Date(value));

const label = (value: string): string => value.replaceAll("_", " ").replaceAll(".", " · ");
const option = (value: string, text: string, selected: string): string =>
  `<option value="${escapeHtml(value)}"${value === selected ? " selected" : ""}>${escapeHtml(text)}</option>`;

const eventDescription = (entry: AuditLogEntry): string => {
  if (entry.action === "user.logged_in") return "Signed in";
  const [actionTarget, ...actionParts] = entry.action.split(".");
  return actionTarget === entry.targetType && actionParts.length
    ? label(actionParts.join("."))
    : label(entry.action);
};

const detailMarkup = (entry: AuditLogEntry): string => `
  <details class="audit-details">
    <summary>Details</summary>
    <dl class="audit-detail-metadata">
      <div><dt>Target</dt><dd>${escapeHtml(entry.targetId ?? "Not specified")}</dd></div>
      <div><dt>Service Type</dt><dd>${escapeHtml(entry.serviceTypeName ?? "System-wide")}</dd></div>
    </dl>
    ${entry.details
      ? `<div class="audit-detail-changes"><strong>Changes and additional information</strong><pre>${escapeHtml(JSON.stringify(entry.details, null, 2))}</pre></div>`
      : '<p class="audit-no-details">No additional changes recorded.</p>'}
  </details>`;

const entryMarkup = (entry: AuditLogEntry): string => `
  <tr class="audit-entry">
    <td><time datetime="${escapeHtml(entry.occurredAt)}">${escapeHtml(formatTime(entry.occurredAt))} ET</time></td>
    <td><strong>${escapeHtml(entry.actorName ?? "System")}</strong>${entry.actorEmail ? `<small>${escapeHtml(entry.actorEmail)}</small>` : ""}</td>
    <td>${escapeHtml(label(entry.targetType))}</td>
    <td>${escapeHtml(eventDescription(entry))}</td>
    <td>${detailMarkup(entry)}</td>
  </tr>`;

export const renderAdminAuditLog = async ({ app, brand, menu }: RenderOptions): Promise<void> => {
  app.innerHTML = `<main class="shell admin-shell audit-shell">
    <header class="topbar page-title-topbar">${brand}<div class="mobile-page-heading" aria-hidden="true"><h1>Audit Log.</h1></div>${menu}</header>
    <section class="admin-intro page-title-intro"><h1>Audit Log.</h1>
      <p class="lede">Review durable application activity and configuration changes.</p></section>
    <form class="audit-filters" id="audit-filters">
      <label><span>From</span><input type="date" name="dateFrom"></label>
      <label><span>Through</span><input type="date" name="dateTo"></label>
      <label><span>Actor</span><select name="actorId"><option value="">All actors</option></select></label>
      <label><span>Action</span><select name="action"><option value="">All actions</option></select></label>
      <label><span>Target</span><select name="targetType"><option value="">All targets</option></select></label>
      <div class="audit-filter-actions"><button class="primary-button compact" type="submit">Apply filters</button><button class="secondary-button compact" type="reset">Clear</button></div>
    </form>
    <div class="audit-result-heading"><p id="audit-count">Loading activity…</p></div>
    <section class="audit-list" id="audit-list" aria-live="polite"></section>
    <nav class="audit-pagination" aria-label="Audit Log pages"><button class="secondary-button compact" id="audit-previous" type="button">Previous</button><span id="audit-page"></span><button class="secondary-button compact" id="audit-next" type="button">Next</button></nav>
  </main>`;
  const form = document.querySelector<HTMLFormElement>("#audit-filters");
  const list = document.querySelector<HTMLElement>("#audit-list");
  const count = document.querySelector<HTMLElement>("#audit-count");
  const pageLabel = document.querySelector<HTMLElement>("#audit-page");
  const previous = document.querySelector<HTMLButtonElement>("#audit-previous");
  const next = document.querySelector<HTMLButtonElement>("#audit-next");
  if (!form || !list || !count || !pageLabel || !previous || !next) return;
  let currentPage = 1;
  let optionsLoaded = false;
  const showError = (error: unknown): void => {
    list.innerHTML = `<p class="error-message">${escapeHtml(error instanceof Error ? error.message : "The Audit Log could not be loaded.")}</p>`;
  };

  const load = async (): Promise<void> => {
    list.innerHTML = '<div class="config-card skeleton"></div>';
    const params = new URLSearchParams({ page: String(currentPage), pageSize: "25" });
    new FormData(form).forEach((value, key) => { if (String(value)) params.set(key, String(value)); });
    const response = await fetch(`/api/admin/audit-log?${params}`, { headers: { Accept: "application/json" } });
    if (!response.ok) throw new Error("The Audit Log could not be loaded.");
    const payload = await response.json() as AuditLogResponse;
    if (!optionsLoaded) {
      const actor = form.elements.namedItem("actorId") as HTMLSelectElement;
      const action = form.elements.namedItem("action") as HTMLSelectElement;
      const target = form.elements.namedItem("targetType") as HTMLSelectElement;
      actor.insertAdjacentHTML("beforeend", payload.filters.actors.map((item) => option(item.id, `${item.name} — ${item.email}`, actor.value)).join(""));
      action.insertAdjacentHTML("beforeend", payload.filters.actions.map((item) => option(item, label(item), action.value)).join(""));
      target.insertAdjacentHTML("beforeend", payload.filters.targetTypes.map((item) => option(item, label(item), target.value)).join(""));
      optionsLoaded = true;
    }
    count.textContent = `${payload.total} audit ${payload.total === 1 ? "entry" : "entries"}`;
    list.innerHTML = payload.entries.length
      ? `<div class="audit-table-scroll"><table class="audit-table"><thead><tr><th scope="col">Date &amp; Time</th><th scope="col">User</th><th scope="col">Event Type</th><th scope="col">Event Description</th><th scope="col">Event Details</th></tr></thead><tbody>${payload.entries.map(entryMarkup).join("")}</tbody></table></div>`
      : '<p class="empty-state">No activity matches these filters.</p>';
    pageLabel.textContent = `Page ${payload.page} of ${payload.pageCount}`;
    previous.disabled = payload.page <= 1;
    next.disabled = payload.page >= payload.pageCount;
  };

  form.addEventListener("submit", (event) => { event.preventDefault(); currentPage = 1; void load().catch(showError); });
  form.addEventListener("reset", () => { currentPage = 1; window.setTimeout(() => void load().catch(showError), 0); });
  previous.addEventListener("click", () => { currentPage -= 1; void load().catch(showError); });
  next.addEventListener("click", () => { currentPage += 1; void load().catch(showError); });
  await load().catch(showError);
};
